import { Link } from "react-router-dom";
import { Youtube, Music2, ExternalLink, Mail, Copy, Check } from "lucide-react";
import { useState } from "react";

const Footer = () => {
  const [copied, setCopied] = useState(false);
  
  const handleCopyEmail = (e) => {
    e.preventDefault();
    navigator.clipboard.writeText("mancuniarebelmag@gmail.com");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <footer className="border-t border-border bg-card" data-testid="footer">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Brand Column */}
          <div className="md:col-span-5">
            <Link 
              to="/" 
              className="font-heading font-black text-3xl uppercase tracking-tighter hover:text-primary transition-colors inline-block"
              data-testid="footer-logo"
            >
              Mancunia Rebél
            </Link>
            <p className="mt-4 text-muted-foreground leading-relaxed max-w-md">
              Empowering creatives across music, fashion, film, photography, and the arts. Based in Manchester, UK - celebrating independent creative expression worldwide.
            </p>
            <div className="flex flex-wrap gap-4 mt-6">
              <a
                href="https://www.youtube.com/@PreachamanTV"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                data-testid="footer-youtube"
              >
                <Youtube className="h-5 w-5" />
                <span className="font-mono text-xs uppercase tracking-wider">YouTube</span>
              </a>
              <a
                href="https://soundcloud.com/mancunia-rebel-magazine"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                data-testid="footer-soundcloud"
              >
                <Music2 className="h-5 w-5" />
                <span className="font-mono text-xs uppercase tracking-wider">SoundCloud</span>
              </a>
              <button
                onClick={handleCopyEmail}
                className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                data-testid="footer-email"
                title="Copy email to clipboard"
              >
                {copied ? <Check className="h-5 w-5 text-green-400" /> : <Mail className="h-5 w-5" />}
                <span className="font-mono text-xs uppercase tracking-wider">
                  {copied ? "Copied!" : "Contact"}
                </span>
              </button>
            </div>
          </div>

          {/* Navigation */}
          <div className="md:col-span-2">
            <h4 className="font-heading font-bold uppercase text-sm tracking-wider mb-4">Navigate</h4>
            <ul className="space-y-3">
              {[
                { href: "/articles", label: "All Articles" },
                { href: "/authors", label: "Writers" },
                { href: "/about", label: "About Us" },
                { href: "/admin", label: "Admin" },
              ].map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-primary transition-colors"
                    data-testid={`footer-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Categories */}
          <div className="md:col-span-2">
            <h4 className="font-heading font-bold uppercase text-sm tracking-wider mb-4">Categories</h4>
            <ul className="space-y-3">
              {[
                { href: "/category/music", label: "Music" },
                { href: "/category/fashion", label: "Fashion" },
                { href: "/category/arts", label: "The Arts" },
                { href: "/category/film", label: "Film" },
              ].map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-primary transition-colors"
                    data-testid={`footer-cat-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* External */}
          <div className="md:col-span-3">
            <h4 className="font-heading font-bold uppercase text-sm tracking-wider mb-4">External</h4>
            <ul className="space-y-3">
              <li>
                <a
                  href="https://www.youtube.com/@PreachamanTV"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-primary transition-colors"
                  data-testid="footer-external-youtube"
                >
                  Preachaman TV
                  <ExternalLink className="h-3 w-3" />
                </a>
              </li>
              <li>
                <a
                  href="https://facebook.com/MancuniaRebelMag"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-primary transition-colors"
                  data-testid="footer-external-facebook"
                >
                  Facebook
                  <ExternalLink className="h-3 w-3" />
                </a>
              </li>
              <li>
                <a
                  href="https://soundcloud.com/mancunia-rebel-magazine"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-primary transition-colors"
                  data-testid="footer-external-soundcloud"
                >
                  SoundCloud
                  <ExternalLink className="h-3 w-3" />
                </a>
              </li>
              <li>
                <a
                  href="https://mancuniarebel.tumblr.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-primary transition-colors"
                  data-testid="footer-external-tumblr"
                >
                  Tumblr Archive
                  <ExternalLink className="h-3 w-3" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-mono text-xs text-muted-foreground uppercase tracking-wider">
            © 2026 Mancunia Rebél. All Rights Reserved. AOP Accredited Member #16338.
          </p>
          <p className="font-mono text-xs text-muted-foreground uppercase tracking-wider">
            Manchester, UK • Established 2014 • Empowering Creatives
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
